import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Tabs } from "antd";
import "./MerchantTabs.scss";

// Reusable top-level navigation for a merchant's public storefront.
//
// Renders tabs based on which merchant addons are enabled (read from
// localStorage merchant_app, written by getMerchantApp redux action):
//   - Shop     -> /:merchantSlug          (always shown)
//   - Services -> /:merchantSlug/bookings (when Services/Bookings app enabled)
//   - Hotel    -> /book/:hotelSlug        (when Hotel PMS / hotel module enabled)
//
// Props:
//   merchantSlug : string         -- the merchant's urewards id (URL segment)
//   activeKey    : 'shop'|'bookings'|'hotel'
//   hotelSlug    : string|null    -- booking_engine_slug; required for the
//                                    hotel tab to be enabled
export const isBookingAddonEnabled = (apps) => {
  if (!Array.isArray(apps)) return false;
  const lower = (s) => (s || "").toString().toLowerCase().trim();
  return apps.some((a) => {
    const name = lower(a?.app?.app_name || a?.app_name || a?.name);
    return (
      name === "services, bookings & appointments" ||
      name.includes("services, bookings") ||
      name.includes("bookings & services") ||
      name.includes("booking") ||
      name.includes("appointment") ||
      name === "services"
    );
  });
};

const MerchantTabs = ({
  merchantSlug,
  activeKey,
  hotelSlug = null,
  hasServices: hasServicesProp,
}) => {
  const history = useHistory();
  const [internalHasServices, setInternalHasServices] = useState(false);
  const [hasHotel, setHasHotel] = useState(false);
  const [resolvedHotelSlug, setResolvedHotelSlug] = useState(hotelSlug);

  useEffect(() => {
    if (hasServicesProp !== undefined) return;

    try {
      const raw = localStorage.getItem("merchant_app");
      if (raw) {
        const apps = JSON.parse(raw) || [];
        setInternalHasServices(isBookingAddonEnabled(apps));
      } else {
        setInternalHasServices(false);
      }
    } catch (e) {
      setInternalHasServices(false);
    }
  }, [hasServicesProp, merchantSlug]);

  const hasServices =
    hasServicesProp !== undefined ? Boolean(hasServicesProp) : internalHasServices;

  useEffect(() => {
    try {
      const raw = localStorage.getItem("merchant_app");
      const lower = (s) => (s || "").toString().toLowerCase();
      if (raw) {
        const apps = JSON.parse(raw) || [];
        setHasHotel(
          apps.some(
            (a) =>
              lower(a?.app?.app_name).includes("hotel") ||
              lower(a?.app?.app_name).includes("pms")
          )
        );
      }

      // Fallback: hotel_module_active on the merchant record.
      const merchantRaw = localStorage.getItem("merchant_details");
      if (merchantRaw) {
        const m = JSON.parse(merchantRaw) || {};
        if (m.hotel_module_active || m.hotel_module_enabled) {
          setHasHotel(true);
        }
        if (!resolvedHotelSlug && m.hotel_booking_engine_slug) {
          setResolvedHotelSlug(m.hotel_booking_engine_slug);
        }
      }
    } catch (e) {
      // ignore parse errors
    }
  }, [resolvedHotelSlug, merchantSlug]);

  const items = [
    {
      key: "shop",
      label: "Shop",
      disabled: false,
    },
  ];

  if (hasServices) {
    items.push({ key: "bookings", label: "Book" });
  }

  if (hasHotel) {
    items.push({
      key: "hotel",
      label: "Reservations",
      disabled: !resolvedHotelSlug && activeKey !== "hotel",
    });
  }

  // Hide the tab bar if there is only one tab to show.
  if (items.length <= 1) return null;

  const onChange = (key) => {
    if (key === activeKey) return;
    if (key === "shop") history.push(`/${merchantSlug}`);
    else if (key === "bookings") history.push(`/${merchantSlug}/bookings`);
    else if (key === "hotel" && resolvedHotelSlug)
      history.push(`/book/${resolvedHotelSlug}`);
  };

  return (
    <div className="merchant-tabs">
      <div className="merchant-tabs__inner">
        <Tabs
          activeKey={activeKey}
          onChange={onChange}
          items={items}
          size="large"
        />
      </div>
    </div>
  );
};

export default MerchantTabs;
